import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { User } from '../../types/user.types';
import * as userApi from '../../api/users';
import * as utilityServiceApi from '../../api/utilityServices';
import { UtilityService } from '../../types/utility.types';

interface SubBrokerStats {
  userId: string;
  name: string;
  companyName?: string;
  level: string;
  totalClients: number;
  totalContracts: number;
  liveContracts: number;
  totalCommissionEarned: number;
  averageCommissionRate: number;
}

const SubBrokerPerformance: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subBrokers, setSubBrokers] = useState<User[]>([]);
  const [stats, setStats] = useState<SubBrokerStats[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [contracts, setContracts] = useState<UtilityService[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all users with sub-broker level
      const allUsers = await userApi.getAllUsers();
      const subBrokerUsers = allUsers.filter((user: User) => user.subBrokerLevel && user.isActive);
      setSubBrokers(subBrokerUsers);

      // Fetch all contracts
      const allContracts = await utilityServiceApi.getAllUtilityServices();
      setContracts(allContracts);

      // Calculate stats for each sub-broker
      const calculatedStats: SubBrokerStats[] = subBrokerUsers.map((user: User) => {
        // Find contracts attributed to this sub-broker
        const userContracts = allContracts.filter(
          contract => contract.subBroker?.userId === user._id
        );

        // Get unique clients
        const uniqueClients = new Set(userContracts.map(c => c.client));

        // Calculate live contracts
        const liveContracts = userContracts.filter(
          c => c.status?.toLowerCase() === 'live'
        );

        // Calculate total commission from actual commission payments
        const totalCommission = userContracts.reduce((sum, contract) => {
          // Sum up actual commission payments if they exist
          if (contract.commissionPayments && contract.commissionPayments.length > 0) {
            const contractCommission = contract.commissionPayments.reduce(
              (cSum, payment) => cSum + (payment.amount || 0), 
              0
            );
            return sum + contractCommission;
          }
          return sum;
        }, 0);

        // Calculate average commission rate
        const avgRate = userContracts.length > 0
          ? userContracts.reduce((sum, c) => sum + (c.subBroker?.commissionRate || 0), 0) / userContracts.length
          : 0;

        return {
          userId: user._id,
          name: `${user.firstName} ${user.lastName}`,
          companyName: user.subBrokerCompanyName,
          level: user.subBrokerLevel || '',
          totalClients: uniqueClients.size,
          totalContracts: userContracts.length,
          liveContracts: liveContracts.length,
          totalCommissionEarned: totalCommission,
          averageCommissionRate: avgRate
        };
      });

      // Sort by total contracts descending
      calculatedStats.sort((a, b) => b.totalContracts - a.totalContracts);
      setStats(calculatedStats);

    } catch (err: any) {
      console.error('Error loading sub-broker performance data:', err);
      setError(err.message || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const handleLevelFilter = (event: SelectChangeEvent) => {
    setSelectedLevel(event.target.value);
  };

  const filteredStats = selectedLevel === 'all'
    ? stats
    : stats.filter(stat => stat.level === selectedLevel);

  // Calculate totals
  const totals = filteredStats.reduce(
    (acc, stat) => ({
      clients: acc.clients + stat.totalClients,
      contracts: acc.contracts + stat.totalContracts,
      liveContracts: acc.liveContracts + stat.liveContracts,
      commission: acc.commission + stat.totalCommissionEarned
    }),
    { clients: 0, contracts: 0, liveContracts: 0, commission: 0 }
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Sub-Broker Performance Report
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Sub-Brokers
              </Typography>
              <Typography variant="h4">
                {filteredStats.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Clients
              </Typography>
              <Typography variant="h4">
                {totals.clients}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Contracts
              </Typography>
              <Typography variant="h4">
                {totals.contracts}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {totals.liveContracts} Live
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Commission
              </Typography>
              <Typography variant="h4">
                £{totals.commission.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Level</InputLabel>
          <Select
            value={selectedLevel}
            label="Filter by Level"
            onChange={handleLevelFilter}
          >
            <MenuItem value="all">All Levels</MenuItem>
            <MenuItem value="Lead Gen">Lead Gen</MenuItem>
            <MenuItem value="Sub Broker">Sub Broker</MenuItem>
            <MenuItem value="Sub Broker Pro">Sub Broker Pro</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Performance Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sub-Broker</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Level</TableCell>
              <TableCell align="right">Clients</TableCell>
              <TableCell align="right">Total Contracts</TableCell>
              <TableCell align="right">Live Contracts</TableCell>
              <TableCell align="right">Avg Commission Rate</TableCell>
              <TableCell align="right">Total Commission</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStats.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No sub-brokers found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredStats.map((stat) => (
                <TableRow key={stat.userId} hover>
                  <TableCell>{stat.name}</TableCell>
                  <TableCell>{stat.companyName || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={stat.level} 
                      size="small"
                      color={
                        stat.level === 'Lead Gen' ? 'default' :
                        stat.level === 'Sub Broker' ? 'primary' :
                        'secondary'
                      }
                    />
                  </TableCell>
                  <TableCell align="right">{stat.totalClients}</TableCell>
                  <TableCell align="right">{stat.totalContracts}</TableCell>
                  <TableCell align="right">{stat.liveContracts}</TableCell>
                  <TableCell align="right">{stat.averageCommissionRate.toFixed(1)}%</TableCell>
                  <TableCell align="right">
                    £{stat.totalCommissionEarned.toLocaleString('en-GB', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SubBrokerPerformance;
